/**
 * App — the studio shell: derives the route table ONCE from the composed genome,
 * owns the history/popstate state machine, and renders the matched route's
 * editor under a Suspense boundary (every editor is a `React.lazy` chunk).
 *
 * Routing contract: the SDK owns genome→component resolution (`resolveRoutes`)
 * and pathname→route matching (`matchRoute`); the APP owns history (pushState +
 * popstate) and feeds `navigate` into the SDK route context. An unmatched path
 * renders a readable not-found (the genome's routes are the bijective truth, so
 * this is only reachable for a hand-typed bad URL).
 *
 * @module @ubiclouds/v5-studio/app/App
 */

import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import {
  matchRoute,
  resolveRoutes,
  RouteParamsProvider,
  type ResolvedRoute,
} from '@ubiclouds/v5-sdk/router';
import type { JumpRoute } from '../ide/jump-routes.js';
import { JumpRoutesProvider } from '../ide/jump-routes-context.js';
import { StudioGenomeProvider } from './studio-genome-context.js';

export interface AppProps {
  readonly genome: GenomeV3;
  readonly plugins: readonly ComposableGenomePlugin[];
  /**
   * The FULL 16-kind genome fed to the {@link StudioGenomeProvider} for the
   * genome-graph panel / generated-index sibling discovery (#705 S3). The router
   * boots the ROUTE-ONLY `genome` (schema-free — the generated clusters carry only
   * routes + stub components on the entry), but the graph panel needs the full
   * data-flow entities, so `main.tsx` lazy-loads the full compose (`genome-full.js`,
   * off the entry) and threads it here. Falls back to `genome` until the lazy full
   * graph resolves (a microtask) — its route table is a superset, so sibling
   * discovery is correct either way.
   */
  readonly graphGenome?: GenomeV3 | null;
}

/**
 * The pre-derivation jump-target value (#1272) — a module-scope FROZEN empty
 * array, so the first render's provider value is a stable ref identical to the
 * context's own default. Never reassigned.
 */
const NO_JUMP_ROUTES: readonly JumpRoute[] = Object.freeze([]);

const shell: CSSProperties = {
  height: '100vh',
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--os-bg)',
  color: 'var(--os-text)',
  overflow: 'hidden',
};

const fallback: CSSProperties = {
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--os-text-muted)',
};

const notFound: CSSProperties = {
  ...fallback,
  flexDirection: 'column',
  gap: '0.5rem',
};

/** Read the current pathname from `window.location` (the SPA is browser-only). */
function currentPath(): string {
  return window.location.pathname || '/';
}

export function App({ genome, plugins, graphGenome }: AppProps): ReactElement {
  // Derive the route table ONCE from the composed genome (gate 4: the router
  // consumes THE genome). Resolution throws on an unresolvable route — surfaced
  // by the boot boundary in main.tsx, never a silent 404.
  const routes: readonly ResolvedRoute[] = useMemo(
    () => resolveRoutes(genome, plugins),
    [genome, plugins],
  );

  // The genome-derived Cmd-K jump targets (#142): one per resolved route,
  // labelled from the route title (path fallback). Fed DOWN to the lazy IDE
  // chunk via context so the IDE never imports the genome barrel (which would
  // drag the dock-registering plugins into its split chunk).
  //
  // #1272 (gate 8, module axis) — the pure derivation module is DYNAMIC-imported
  // rather than statically pulled onto the eager entry: its ONLY consumer is the
  // LAZY `/ide` chunk, so nothing at first paint reads the result. The targets
  // therefore land one microtask after mount, into state; the initial value is
  // the SAME frozen empty array the context already defaults to, so a render
  // before the import resolves is byte-identical to a render with no provider —
  // and the `/ide` chunk cannot possibly have mounted by then. Stable refs on
  // both sides (a frozen module constant, then the frozen `deriveJumpRoutes`
  // output) → `useSyncExternalStore`/selector-loop safe.
  const [jumpRoutes, setJumpRoutes] = useState<readonly JumpRoute[]>(NO_JUMP_ROUTES);
  useEffect(() => {
    let alive = true;
    void import('../ide/jump-routes.js').then(({ deriveJumpRoutes }) => {
      if (alive) setJumpRoutes(deriveJumpRoutes(routes));
    });
    return () => {
      alive = false;
    };
  }, [routes]);

  // #1488 — PUBLISH the resolved route table's paths to the agent's PERCEIVE
  // seam. A generated family's head channel reads that family's persisted
  // collections, so it cannot tell "the user has no rows" from "this build has no
  // such surface"; the published paths are what let it say which. DYNAMIC-imported
  // for the #1272 reason above — the seam's only consumers are LAZY agent chunks,
  // so nothing at first paint reads it, and the eager closure is untouched.
  // FAIL-SOFT by construction: until this resolves the channels answer `unknown`,
  // which never renders as "mounted".
  useEffect(() => {
    let alive = true;
    const paths = routes.map((r) => r.path);
    void import('../agent/route-table-ref.js').then(({ setResolvedRoutePathsRef }) => {
      if (alive) setResolvedRoutePathsRef(paths);
    });
    // No cleanup CLEAR: unlike `navigate-ref`, a stale route list cannot FIRE
    // anything, and a restage re-runs this effect with the new table. Clearing
    // asynchronously would risk racing a newer publish to `null` — i.e. turning a
    // measured `mounted` back into `unknown` for no gain.
    return () => {
      alive = false;
    };
  }, [routes]);

  // The history/popstate state machine: pathname is the single source of truth;
  // a primitive string in state (stable-ref selector discipline).
  const [path, setPath] = useState<string>(currentPath);

  useEffect(() => {
    const onPop = (): void => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string): void => {
    if (to === window.location.pathname) return;
    window.history.pushState({}, '', to);
    setPath(to);
  }, []);

  const match = useMemo(() => matchRoute(routes, path), [routes, path]);

  const routeContext = useMemo(
    () => ({ params: match?.params ?? {}, navigate }),
    [match, navigate],
  );

  if (!match) {
    return (
      <div style={shell} data-testid="studio-shell">
        <div style={notFound} data-testid="studio-not-found">
          <strong>No route for {path}</strong>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              padding: '0.4rem 0.75rem',
              background: 'var(--os-accent)',
              color: 'var(--os-text)',
              border: '1px solid var(--os-border)',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Go home
          </button>
        </div>
      </div>
    );
  }

  const Routed = match.route.Component;
  return (
    <div style={shell} data-testid="studio-shell">
      <RouteParamsProvider value={routeContext}>
        <StudioGenomeProvider value={graphGenome ?? genome}>
          <JumpRoutesProvider value={jumpRoutes}>
            <Suspense fallback={<div style={fallback} data-testid="studio-loading">Loading…</div>}>
              <Routed />
            </Suspense>
          </JumpRoutesProvider>
        </StudioGenomeProvider>
      </RouteParamsProvider>
    </div>
  );
}
